import { useCallback } from 'react';
import Button from '@components/ui/Button';
import { ModalShell } from './shared';
import type { StoreSearchResult } from '../../../types/api.types';

const DIALOG_COPY = {
    TITLE: 'Remove dashboard expiry?',
    BODY: 'Selected trial stores will go live. Unchecked stores stay in trial.',
    CONFIRM: 'Remove expiry',
    CANCEL: 'Cancel',
} as const;

interface TrialStoreCheckboxRowProps {
    store: StoreSearchResult;
    isChecked: boolean;
    isLoading: boolean;
    onToggle: (storeId: string) => void;
}

function TrialStoreCheckboxRow({
    store,
    isChecked,
    isLoading,
    onToggle,
}: TrialStoreCheckboxRowProps) {
    const handleChange = useCallback(() => {
        onToggle(store._id);
    }, [onToggle, store._id]);

    const inputId = `make-live-store-${store._id}`;

    return (
        <label
            htmlFor={inputId}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                isChecked
                    ? 'border-[#c4b5fd] bg-[#f5f3ff] text-[#5b21b6]'
                    : 'border-[#e2e8f0] bg-[#f8fafc] text-[#475569] hover:border-[#cbd5e1]'
            } ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
            <input
                id={inputId}
                type="checkbox"
                checked={isChecked}
                onChange={handleChange}
                disabled={isLoading}
                className="h-4 w-4 rounded border-[#cbd5e1] text-[#6e64f0] focus:ring-[#6e64f0]"
            />
            <span className="text-sm font-medium truncate">{store.name}</span>
        </label>
    );
}

interface MakeTrialStoresLiveDialogProps {
    isOpen: boolean;
    stores: StoreSearchResult[];
    selectedStoreIds: string[];
    isLoading: boolean;
    onToggleStore: (storeId: string) => void;
    onConfirm: () => void;
    onCancel: () => void;
}

export default function MakeTrialStoresLiveDialog({
    isOpen,
    stores,
    selectedStoreIds,
    isLoading,
    onToggleStore,
    onConfirm,
    onCancel,
}: MakeTrialStoresLiveDialogProps) {
    const selectedIdSet = new Set(selectedStoreIds);

    const handleClose = useCallback(() => {
        if (!isLoading) {
            onCancel();
        }
    }, [isLoading, onCancel]);

    const footer = (
        <>
            <Button variant="secondary" className="flex-1" onClick={onCancel} disabled={isLoading}>
                {DIALOG_COPY.CANCEL}
            </Button>
            <Button
                variant="destructive"
                className="flex-1"
                isLoading={isLoading}
                onClick={onConfirm}
            >
                {DIALOG_COPY.CONFIRM}
            </Button>
        </>
    );

    return (
        <ModalShell
            isOpen={isOpen}
            title={DIALOG_COPY.TITLE}
            onClose={handleClose}
            footer={footer}
            maxWidth="sm"
            elevation="nested"
        >
            <p className="text-sm text-[#64748b] mb-4">{DIALOG_COPY.BODY}</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
                {stores.map((store) => (
                    <TrialStoreCheckboxRow
                        key={store._id}
                        store={store}
                        isChecked={selectedIdSet.has(store._id)}
                        isLoading={isLoading}
                        onToggle={onToggleStore}
                    />
                ))}
            </div>
        </ModalShell>
    );
}
